import Button from "@/components/Button";
import Card from "@/components/Card";
import ReviewForm from "@/components/microsite/ReviewForm";
import { ModalHeader } from "@/components/Modal";
import { Skeleton } from "@/components/ui/skeleton";
import { getPaymentMethodName } from "@/lib/paymentMethod";
import assetUrl from "@/vars/assetUrl";

import dayjs from "dayjs";
import {
  ArrowUpRightSquareIcon,
  CheckCircleIcon,
  CreditCardIcon,
  InfoIcon,
  Loader2Icon,
  NotebookIcon,
  StoreIcon,
  XCircleIcon,
} from "lucide-react";
import { Fragment, useEffect, useMemo, useRef } from "react";
import useSWR from "swr";

export default function CompletePaymentModal({
  modalProp,
  hash,
  closePaymentModal,
  noClose = false,
}) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const prevStatus = useRef<any>(null);
  const { hashId, ret } = useMemo(() => {
    const objs = { hashId: null, ret: null };
    if (hash.length > 0) {
      // to meessyy
      const [hashId, ret] = hash.split("#payment=")[1]?.split("&");
      objs.hashId = hashId;
      objs.ret = ret ? ret.split("=")[1] : null;
    }
    return objs;
  }, [hash]);

  const { data, isLoading } = useSWR(
    hash
      ? "/microsite/shop/transaction?" +
          new URLSearchParams({ hash: hashId, ret })
      : null,
    {
      errorRetryCount: 0,
    }
  );

  useEffect(() => {
    if (data?.data) {
      if (prevStatus.current === "UNPAID" && data?.data?.status === "PAID") {
        //@ts-ignore
        if (typeof window.fbq !== "undefined") {
          const items = [];
          for (const inv of data?.data?.invoices) {
            for (const itm of inv.items) items.push(itm?.item_id);
          }

          //@ts-ignore
          window.fbq("track", "Purchase", {
            value: data?.data?.grand_total,
            currency: "IDR",
            content_ids: items,
            content_type: "product",
          });
        }
      }
      prevStatus.current == data.data.status;
    }
  }, [data?.data]);

  if (isLoading) {
    return (
      <div className="p-2 min-h-[60dvh]">
        <Skeleton className="w-full h-5" />
        <Skeleton className="mt-6 w-full h-[300px]" />
        <Skeleton className="mt-6 w-full h-12" />
        <Skeleton className="mt-3 w-full h-12" />
      </div>
    );
  }

  if (data?.data) {
    return (
      <Fragment>
        {data?.data?.status === "UNPAID" ? (
          <Fragment>
            <ModalHeader
              {...modalProp}
              disabled
              hideClose
              className="flex items-center"
            >
              <CreditCardIcon className="mr-2 size-5" /> Payment
            </ModalHeader>
            <div className="relative">
              <div className="top-0 right-0 bottom-0 left-0 absolute flex flex-col justify-center items-center bg-white rounded-md w-full h-full">
                <Loader2Icon className="size-9 animate-spin" />
                <p className="mt-4">Please wait...</p>
              </div>
              <div className="-mx-3">
                <iframe
                  src={data.data.invoice_url}
                  ref={iframeRef}
                  sandbox="allow-forms allow-scripts allow-same-origin allow-top-navigation"
                  className="z-10 relative bg-white rounded-b-md w-full h-[800px]"
                />
              </div>
            </div>
            <Button
              onClick={closePaymentModal}
              className="w-full"
              variant="outline"
            >
              Cancel Payment
            </Button>
          </Fragment>
        ) : data?.data?.status === "PAID" ? (
          <Fragment>
            <div className="flex flex-col justify-center items-center mt-4 mb-4 text-center">
              <CheckCircleIcon className="size-7 text-green-500" />
              <h3 className="mt-4 font-montserrat font-bold text-foreground/80 text-lg">
                Your payment has been successful!
              </h3>
            </div>
            <div className="mb-6 text-foreground/80 text-sm text-center">
              Your payment with No: <strong>{data.data.transaction_no}</strong>{" "}
              has been successfully processed.
            </div>

            <div className="mb-3 text-sm">
              <div className="flex mb-1">
                <dt className="w-32 font-bold">Transaction No</dt>
                <dd>: {data.data.transaction_no}</dd>
              </div>
              <div className="flex mb-1">
                <dt className="w-32 font-bold">Name</dt>
                <dd>: {data.data.full_name}</dd>
              </div>
              <div className="flex mb-1">
                <dt className="w-32 font-bold">Email</dt>
                <dd>: {data.data.email}</dd>
              </div>
              <div className="flex mb-1">
                <dt className="w-32 font-bold">Phone</dt>
                <dd>: {data.data.phone}</dd>
              </div>
              <div className="flex mb-1">
                <dt className="w-32 font-bold">Payment Method</dt>
                <dd>: {getPaymentMethodName(data.data.payment_method)}</dd>
              </div>
              <div className="flex mb-1">
                <dt className="w-32 font-bold">Paid At</dt>
                <dd>
                  : {dayjs(data.data.paid_at).format("DD MMMM YYYY HH:mm")}
                </dd>
              </div>
            </div>

            <div className="">
              {data.data.invoices.map((item, index) => (
                <InvoiceDetailComponent
                  item={item}
                  hashId={hashId}
                  key={index}
                  fullName={data.data.full_name}
                />
              ))}
            </div>

            <CompleteSummary data={data.data} />

            <div className="hidden mt-4 w-full overflow-auto text-sm">
              <pre>{JSON.stringify(data.data, null, 2)}</pre>
            </div>

            <Card className="mb-3 p-3 text-sm">
              <InfoIcon className="inline-block -mt-1 mr-1 w-4" />
              We also send completed reciept and all information into your
              email. So you can close this window and check your email for more
              detailed purchase information.
            </Card>

            {!noClose && (
              <Button
                onClick={closePaymentModal}
                className="w-full"
                variant="outline"
              >
                Close
              </Button>
            )}
          </Fragment>
        ) : (
          <Fragment>
            <div className="text-center">
              <XCircleIcon className="mx-auto size-7 text-red-500" />
              <h2 className="mt-1 font-montserrat font-semibold text-xl">
                Expired!
              </h2>
              <p className="mb-3 font-worksans text-foreground/60">
                Transaction has been expired
              </p>
              <Button
                onClick={closePaymentModal}
                className="w-full"
                variant="outline"
              >
                Close
              </Button>
            </div>
          </Fragment>
        )}
        {/* <Button className="mb-3 w-full" variant="dark">
          Check Payment Status
        </Button> */}
      </Fragment>
    );
  }

  return (
    <Fragment>
      <div className="flex flex-col justify-center items-center py-10">
        <XCircleIcon className="mb-2 size-7 text-red-500" />
        <h3>Transaction not found</h3>
      </div>
    </Fragment>
  );
}

function InvoiceDetailComponent({ item, hashId, fullName }) {
  return (
    <div className="mb-2 p-2 border rounded-md">
      <div className="flex pb-1">
        <h2 className="flex-1 font-bold text-sm">
          <StoreIcon className="inline-block -mt-1 mr-0.5 size-4" />{" "}
          {item?.shop_tenant?.name || "-"}
        </h2>
        <h2 className="font-bold text-foreground/60 text-sm">
          {item.invoice_no}
        </h2>
      </div>
      <div className="-mb-2">
        {item.items.map((item, index) => (
          <div key={index} className="py-2 border-t w-full">
            <div className="flex w-full">
              <div>
                <img
                  src={assetUrl + item.item.images[0].image}
                  className="rounded size-14 object-contain"
                />
              </div>
              <div className="flex-1 pr-2 pl-2 shrink-0">
                <h3 className="font-bold text-sm">{item.item.name}</h3>
                <p className="text-foreground/70 text-sm">
                  Rp
                  {new Intl.NumberFormat("id").format(item.price)} @ {item.qty}
                </p>

                {item?.notes ? (
                  <p className="mt-1 text-foreground/70 text-xs">
                    <NotebookIcon className="inline-block -mt-1 size-3" /> Note:{" "}
                    {item.notes}
                  </p>
                ) : (
                  <Fragment />
                )}
              </div>
              <div className="shrink-0">
                <p className="font-bold text-foreground/80 text-sm text-right">
                  Rp {Intl.NumberFormat("id").format(item.total)}
                </p>
              </div>
            </div>
            <div className="flex gap-2 mt-1 pt-1 border-t">
              <ReviewForm
                hashId={hashId}
                itemId={item.item_id}
                fullName={fullName}
              />

              {item.item.product_type === "download" && (
                <div className="flex flex-1 justify-end">
                  <a href={item.item.download_link} target="_blank">
                    <Button size="sm" variant="blue" className="mt-1 text-xs">
                      Get a file <ArrowUpRightSquareIcon className="size-3" />
                    </Button>
                  </a>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CompleteSummary({ data }) {
  return (
    <div className="mt-3 text-sm">
      <h3 className="font-bold text-foreground/80">Summary</h3>
      <table className="mb-4 w-full text-sm">
        <tbody>
          <tr>
            <td className="py-1 font-semibold text-foreground/50">Sub Total</td>
            <td className="py-1 w-[1%] text-right truncate">
              Rp {Intl.NumberFormat("id").format(data.sub_total)}
            </td>
          </tr>
          <tr>
            <td className="py-1 font-semibold text-foreground/50">Discount</td>
            <td className="py-1 w-[1%] text-right truncate">
              Rp {Intl.NumberFormat("id").format(data.discount || 0)}
            </td>
          </tr>
          <tr>
            <td className="py-1 font-semibold text-foreground/50">
              Payment Fee
            </td>
            <td className="py-1 w-[1%] text-right truncate">
              Rp {Intl.NumberFormat("id").format(data.payment_fee || 0)}
            </td>
          </tr>
          <tr>
            <td className="py-1 border-t font-semibold text-foreground text-base">
              Grand Total
            </td>
            <td className="py-1 border-t w-[1%] font-bold text-base text-right truncate">
              Rp {Intl.NumberFormat("id").format(data.grand_total)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
